/**
 * One table of interface copy, three languages, one way to read it.
 *
 * Import this as "@/lib/i18n/index" — spelled out. The old lib/i18n.ts is
 * still beside this directory until block 5c deletes it, and a bare
 * "@/lib/i18n" resolves to that file first, silently handing back the old
 * strings. tests/shell-guards.test.ts refuses the bare form.
 *
 * The language of a request is a cookie (`lang`), read once in the root
 * layout and passed down; every page ships one language, never three
 * (BLUEPRINT §5: server-rendered, not mist.js's show/hide pairs).
 */
import { LANGS, type Lang } from "../domain/lang";
import { isStatus, stood } from "../domain/status";
import { de } from "./de";
import { en } from "./en";
import { zh } from "./zh";

export type { Lang };
export type Key = keyof typeof zh;
export type Table = Readonly<Record<Key, string>>;

export const TABLES: Record<Lang, Table> = { zh, en, de };

/** The one mapping from a language to an `Intl` locale: the calendars (the
 *  home's and the interviews page's, components/interviews/when.ts), the
 *  settings' weekdays and "export my data" all take it from here — a second
 *  copy of this table is how two calendars start disagreeing about what a
 *  Tuesday is called. */
export const LOCALE: Record<Lang, string> = { zh: "zh-CN", en: "en-GB", de: "de-DE" };

/** The weekdays' short names, Monday first: the month grid's heads and the
 *  morning letter's days. */
export function weekdayNames(lang: Lang): string[] {
  const f = new Intl.DateTimeFormat(LOCALE[lang], { timeZone: "UTC", weekday: "short" });
  // 2026-01-05 is a Monday.
  return Array.from({ length: 7 }, (_, i) => f.format(new Date(Date.UTC(2026, 0, 5 + i))));
}

/** The languages the switcher offers. German is not complete — 65 of the 74
 *  `ext.*` strings are still the English ones — and it is off until somebody
 *  reads it through; putting "de" back here is the whole of turning it on.
 *
 *  Anything rendered for a person from `users.lang` goes through `offered()`:
 *  the interface's own language comes from the cookie and `langOf` already
 *  answers with an offered one, but `users.lang` is the *data* language and
 *  may say `de` (domain/lang.ts LANGS has it, the cutover can import it). The
 *  extension's panel took that value straight to `table()` — a German panel of
 *  65 English strings beside a Chinese portal.
 *
 *  Messages sent through a channel do not either: they go through
 *  `channelLang()` below, which answers `users.lang` itself (#334).
 *
 *  Two more readers deliberately do not: `platform/commands.ts` writes confirmed
 *  rules into `profile_doc`, and `platform/chat.ts` names a thread when no
 *  interface said which language the turn was in (Slack, WhatsApp — a turn
 *  from the portal or the extension names it in theirs, #255 PR 6). Those are
 *  the reader's own records, and a German one is written in German rather than
 *  in whatever the interface happens to be able to render. */
export const OFFERED = ["zh", "en"] as const satisfies readonly Lang[];
export type Offered = (typeof OFFERED)[number];
export const DEFAULT_LANG: Offered = "zh";

/** How each language names itself in the menu — never translated. */
export const LANG_NAME: Record<Lang, string> = { zh: "中文", en: "English", de: "Deutsch" };

/** The language to actually render in: theirs when it is offered, the default
 *  when it is not. One door, so a half-translated language cannot reach a
 *  person through whichever reader forgot to check. */
export function offered(lang: Lang): Offered {
  return isOffered(lang) ? lang : DEFAULT_LANG;
}

/** What the channels send — Slack's replies, modals and receipts, the
 *  connected line, WhatsApp's link replies, the channel chat's fixed lines —
 *  is written in `users.lang`, not in an offered interface language (#334).
 *  A German account used to read English mornings from the pipeline and
 *  Chinese receipts from here.
 *
 *  The channel table is every key a channel reader takes from these tables.
 *  tests/channel-lang.test.ts reads those readers' source and holds each key
 *  they name to this list. */
// `action.confirm.` is the line a channel chat's confirm writes under the reply
// (platform/adapters/chat-tools.ts confirmLine).
const CHANNEL_PREFIXES = ["press.", "slack.", "chan.", "status.", "reason.", "action.confirm."] as const;
// The card's apply link, which a Telegram morning's button says once they
// mean to apply (#626); its three answers have short words of their own,
// press.pick.* (#765).
const CHANNEL_EXTRA: readonly Key[] = ["chat.hereIsFile", "chat.afterActions", "chat.cutCeiling", "chat.cutStopped", "card.goApplyArrow"];
export const CHANNEL_KEYS: readonly Key[] = (Object.keys(zh) as Key[])
  .filter((k) => CHANNEL_PREFIXES.some((p) => k.startsWith(p)) || CHANNEL_EXTRA.includes(k));

/** Channel keys a translation may honestly spell as English does. A table is
 *  typed complete, so an untranslated string is the English one copied in
 *  (the `ext.*` strings above); a word that is the same in both goes here. */
export const SAME_AS_EN: readonly Key[] = [];

const holes = (s: string) => [...s.matchAll(/%\d/g)].map((m) => m[0]).sort().join();

/** The channel keys `lang` has not translated yet: empty, still the English
 *  string, or not filling the same `%1`… as English (a `fmt` would drop what
 *  it was given). Chinese is the source table and English the fallback:
 *  neither can lack one. */
export function channelMissing(lang: Lang, tables: Record<Lang, Table> = TABLES): Key[] {
  if (lang === "zh" || lang === "en") return [];
  const own = tables[lang], en = tables.en;
  return CHANNEL_KEYS.filter((k) => !own[k]?.trim()
    || (own[k] === en[k] && !SAME_AS_EN.includes(k))
    || holes(own[k]) !== holes(en[k]));
}

const warned = new Set<string>();

/** The language a channel message goes out in: `users.lang` when its whole
 *  channel table is translated, English otherwise — a whole message, never a
 *  German one with English holes, and never a message that fails to go.
 *  Whole table, not the keys this message happens to use: a language is on
 *  for the channels or it is not. Said once per process per language. */
export function channelLang(lang: unknown): Lang {
  if (!isLang(lang)) {
    const name = String(lang);
    if (!warned.has(name)) { warned.add(name); console.warn(`[i18n] channel: unknown language ${JSON.stringify(lang)}, sending English`); }
    return "en";
  }
  const missing = channelMissing(lang);
  if (missing.length === 0) return lang;
  if (!warned.has(lang)) {
    warned.add(lang);
    console.warn(`[i18n] channel: ${lang} lacks ${missing.length} translation(s) (${missing.slice(0, 5).join(", ")}${missing.length > 5 ? ", …" : ""}), sending English`);
  }
  return "en";
}

export const COOKIE = "lang";

/** Offered, not merely known: a cookie left over from when German was on
 *  falls back to the default rather than serving a language nobody checks. */
export function isOffered(v: unknown): v is Offered {
  return typeof v === "string" && (OFFERED as readonly string[]).includes(v);
}

export function langOf(cookie: string | undefined): Offered {
  return isOffered(cookie) ? cookie : DEFAULT_LANG;
}

export function isLang(v: unknown): v is Lang {
  return typeof v === "string" && (LANGS as readonly string[]).includes(v);
}

export function t(lang: Lang): Table {
  return TABLES[lang];
}

/** A status as the pages name it (#310): the label, never the key —
 *  「interview_requested」 is a column value, not a word. Null for `new` and
 *  anything unknown: the interface has no word for "nothing decided"
 *  (domain/status.ts `Stood`). For what reaches a person through the model
 *  or a tool's line, where no component renders it. */
export function statusLabel(lang: Lang, s: string | null | undefined): string | null {
  return isStatus(s) && stood(s) ? TABLES[lang][`status.${s}`] : null;
}

/** A board's system as a person reads it: the system's own name — Greenhouse,
 *  Personio — except where there is no system, only their careers page read
 *  as a board (`page`, #576), which has a word of its own (`ats.<name>`). The
 *  extension's labels carry the same keys (adapters/ext.ts `labels`), so the
 *  panel says it in the same words. */
export function atsLabel(s: Table, ats: string): string {
  return (s as Readonly<Record<string, string>>)[`ats.${ats}`] ?? ats;
}

/** `%1`, `%2` … replaced in order. Positional, because the languages put
 *  the count and the date in different places. */
export function fmt(s: string, ...args: (string | number)[]): string {
  return s.replace(/%(\d)/g, (_, i) => String(args[Number(i) - 1] ?? ""));
}
